import { db } from "@/lib/firebase";
import { GoogleSearchConsoleAccount } from "@/schemas/firestore/googleSearchConsole.schema";
import { useStore } from "@/store";
import { FirebaseGoogleSearchConsoleAccount } from "@/types/GoogleSearchConsole";
import {
  collection,
  doc,
  onSnapshot,
  query,
  Timestamp,
  Unsubscribe,
} from "firebase/firestore";
import { useCallback, useEffect, useRef } from "react";

export const useGoogleSearchConsoleData = (userId: string | null) => {
  const {
    setGscAccounts,
    addGscAccount,
    updateGscAccount,
    removeGscAccount,
    setGscUIState,
    setGscErrorForAccount,
    gscAccounts,
  } = useStore();

  const unsubscribeRef = useRef<Unsubscribe | null>(null);
  const lastUserIdRef = useRef<string | null>(null);

  const convertFirebaseAccount = (
    firebaseAccount: FirebaseGoogleSearchConsoleAccount,
  ): GoogleSearchConsoleAccount => ({
    account_id: firebaseAccount.account_id,
    account_email: firebaseAccount.account_email,
    account_name: firebaseAccount.account_name ?? null,
    token_ref: firebaseAccount.token_ref ?? null,
    sites: firebaseAccount.sites ?? [],
    client_id: firebaseAccount.client_id,
    status: firebaseAccount.status ?? "connected",
    error_message: firebaseAccount.error_message ?? null,
    created_at: firebaseAccount.created_at ?? null,
    updated_at: firebaseAccount.updated_at ?? null,
  });

  const toFirebaseAccount = (
    accountId: string,
    data: Omit<FirebaseGoogleSearchConsoleAccount, "account_id">,
  ): FirebaseGoogleSearchConsoleAccount => ({
    ...data,
    account_id: accountId,
    created_at:
      data.created_at instanceof Timestamp
        ? data.created_at.toDate()
        : data.created_at
          ? new Date(data.created_at)
          : undefined,
    updated_at:
      data.updated_at instanceof Timestamp
        ? data.updated_at.toDate()
        : data.updated_at
          ? new Date(data.updated_at)
          : undefined,
  });

  const subscribeToAccounts = useCallback(() => {
    if (!userId) {
      setGscAccounts([]);
      return;
    }

    setGscUIState({ isLoadingAccounts: true });

    try {
      const accountsRef = collection(
        db,
        `users/${userId}/integrations/google_search_console/accounts`,
      );
      const accountsQuery = query(accountsRef);

      unsubscribeRef.current = onSnapshot(
        accountsQuery,
        (snapshot) => {
          const accountsData = snapshot.docs.map((accountDoc) => {
            const data = accountDoc.data() as Omit<
              FirebaseGoogleSearchConsoleAccount,
              "account_id"
            >;
            return convertFirebaseAccount(
              toFirebaseAccount(accountDoc.id, data),
            );
          });

          setGscAccounts(accountsData);
          setGscUIState({ isLoadingAccounts: false });
        },
        (error) => {
          console.error(
            "GSC Data Hook: Firebase accounts subscription error:",
            error,
          );
          setGscUIState({ isLoadingAccounts: false });
        },
      );
    } catch (error) {
      console.error("Failed to subscribe to GSC accounts:", error);
      setGscUIState({ isLoadingAccounts: false });
    }
  }, [userId, setGscAccounts, setGscUIState]);

  const subscribeToAccount = useCallback(
    (accountId: string) => {
      if (!userId) return null;

      const accountRef = doc(
        db,
        `users/${userId}/integrations/google_search_console/accounts/${accountId}`,
      );

      return onSnapshot(
        accountRef,
        (accountDoc) => {
          if (accountDoc.exists()) {
            const data = accountDoc.data() as Omit<
              FirebaseGoogleSearchConsoleAccount,
              "account_id"
            >;
            const account = convertFirebaseAccount(
              toFirebaseAccount(accountDoc.id, data),
            );
            const existingAccount = gscAccounts.find(
              (existing) => existing.account_id === accountId,
            );
            if (existingAccount) {
              updateGscAccount(accountId, account);
            } else {
              addGscAccount(account);
            }
          } else {
            removeGscAccount(accountId);
          }
        },
        (error) => {
          console.error(
            `Firebase GSC account ${accountId} subscription error:`,
            error,
          );
          setGscErrorForAccount(
            accountId,
            `Failed to sync account: ${error.message}`,
          );
        },
      );
    },
    [
      userId,
      gscAccounts,
      addGscAccount,
      updateGscAccount,
      removeGscAccount,
      setGscErrorForAccount,
    ],
  );

  const getAccountById = useCallback(
    (accountId: string): GoogleSearchConsoleAccount | null => {
      return (
        gscAccounts.find((account) => account.account_id === accountId) || null
      );
    },
    [gscAccounts],
  );

  const getConnectedAccounts = useCallback(() => {
    return gscAccounts.filter((account) => account.status === "connected");
  }, [gscAccounts]);

  const hasConnectedAccounts = useCallback(() => {
    return getConnectedAccounts().length > 0;
  }, [getConnectedAccounts]);

  const getSitesForAccount = useCallback(
    (accountId: string) => {
      return getAccountById(accountId)?.sites || [];
    },
    [getAccountById],
  );

  const getAccountCount = useCallback(() => {
    return gscAccounts.length;
  }, [gscAccounts]);

  useEffect(() => {
    if (lastUserIdRef.current === userId) return;
    lastUserIdRef.current = userId;

    unsubscribeRef.current?.();
    unsubscribeRef.current = null;

    if (userId) {
      subscribeToAccounts();
    } else {
      setGscAccounts([]);
    }

    return () => {
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [userId, subscribeToAccounts, setGscAccounts]);

  return {
    accounts: gscAccounts,
    subscribeToAccount,
    getAccountById,
    getConnectedAccounts,
    hasConnectedAccounts,
    getSitesForAccount,
    getAccountCount,
  };
};
